import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Testimonial, TestimonialInsert } from '@/services/content'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2, Star } from 'lucide-react'
import { ImageUpload } from './ImageUpload'

const formSchema = z.object({
  author_name: z.string().min(2, 'Name is required'),
  author_role: z.string().optional(),
  content: z.string().min(10, 'Content must be at least 10 characters'),
  author_avatar_url: z.string().nullable().optional(),
  rating: z.coerce.number().min(1).max(5),
  sort_order: z.coerce.number().int().default(0),
})

interface TestimonialDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialData?: Testimonial | null
  onSubmit: (data: TestimonialInsert) => Promise<void>
  isSubmitting: boolean
  locale: string
}

export const TestimonialDialog = ({
  open,
  onOpenChange,
  initialData,
  onSubmit,
  isSubmitting,
  locale,
}: TestimonialDialogProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      author_name: '',
      author_role: '',
      content: '',
      author_avatar_url: null,
      rating: 5,
      sort_order: 0,
    },
  })

  useEffect(() => {
    if (initialData) {
      form.reset({
        author_name: initialData.author_name,
        author_role: initialData.author_role || '',
        content: initialData.content,
        author_avatar_url: initialData.author_avatar_url,
        rating: initialData.rating,
        sort_order: initialData.sort_order,
      })
    } else {
      form.reset({
        author_name: '',
        author_role: '',
        content: '',
        author_avatar_url: null,
        rating: 5,
        sort_order: 0,
      })
    }
  }, [initialData, form, open])

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    await onSubmit({
      ...values,
      author_role: values.author_role || null, // Ensure empty string becomes null if desired, or keep as string
      locale,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialData ? 'Edit Testimonial' : 'New Testimonial'} (
            {locale.toUpperCase()})
          </DialogTitle>
          <DialogDescription>
            Manage customer reviews and testimonials.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="author_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Author Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="author_role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="CEO, Manager..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="The testimonial text..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="rating"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rating</FormLabel>
                    <Select
                      onValueChange={(val) => field.onChange(parseInt(val))}
                      defaultValue={field.value.toString()}
                      value={field.value.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select rating" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {[5, 4, 3, 2, 1].map((rating) => (
                          <SelectItem key={rating} value={rating.toString()}>
                            <div className="flex items-center gap-2">
                              <span>{rating}</span>
                              <div className="flex">
                                {Array.from({ length: rating }).map((_, i) => (
                                  <Star
                                    key={i}
                                    className="w-3 h-3 fill-current"
                                  />
                                ))}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sort_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sort Order</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="author_avatar_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Author Avatar</FormLabel>
                  <FormControl>
                    <ImageUpload
                      value={field.value}
                      onChange={field.onChange}
                      label="Upload Avatar"
                      className="w-32" // Limit width for avatar upload preview context
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
